<template>
  <div class="order-container" v-loading.fullscreen.lock="display.isLoading">
    <div class="centre">
      <h3>{{ display.title }}</h3>
      <h4 v-if="display.isDetail">
        {{ order.orderNumber }}
        <span class="order__status" :class="status[order.status].styleClazz">
          {{ status[order.status].display }}
        </span>
      </h4>
    </div>

    <!-- Order creation line items -->
    <div
      class="line-items"
      v-if="
        !display.isDetail &&
        shoppingCart.shoppingCart.lineItems &&
        shoppingCart.shoppingCart.lineItems.length > 0
      "
    >
      <el-row class="line-item line-item-header">
        <el-col :span="2">
          <strong>#</strong>
        </el-col>
        <el-col :span="10">
          <strong>Item</strong>
        </el-col>
        <el-col :span="5" class="centre">
          <strong>Unit Price</strong>
        </el-col>
        <el-col :span="2" class="centre">
          <strong>Qty</strong>
        </el-col>
        <el-col :span="5" class="centre">
          <strong>Total</strong>
        </el-col>
      </el-row>
      <el-row
        class="line-item"
        v-for="(item, index) in shoppingCart.shoppingCart.lineItems"
        :key="item._id"
      >
        <el-col :span="2">{{ index + 1 }}</el-col>
        <el-col :span="10" class="line-item__name">
          <div>
            {{ item.name }}
            <span v-if="item.isSpecificationEnabled">
              ({{ item.specificationSelection.name }})
            </span>
          </div>
          <div v-for="(sel, selIdx) in item.packageSelections" :key="selIdx">
            <div v-for="key in Object.keys(sel)" :key="key">
              <p>
                <strong>{{ key }}</strong>
              </p>
              <p>{{ sel[key].join(", ") }}</p>
            </div>
          </div>
        </el-col>
        <el-col :span="5" class="centre">{{ item.unitNetPriceDisplay }}</el-col>
        <el-col :span="2" class="centre">{{ item.quantity }}</el-col>
        <el-col :span="5" class="centre"
          >${{ item.totalGrossPriceDisplay }}</el-col
        >
      </el-row>
      <el-row class="line-item">
        <el-col :span="2">
          <span class="invisible">1</span>
        </el-col>
        <el-col :span="17">
          <strong>Subtotal</strong>
        </el-col>
        <el-col :span="5" class="centre">
          <strong>${{ subtotal }}</strong>
        </el-col>
      </el-row>
      <el-row v-if="isDelivery(order.mode)" class="line-item">
        <el-col :span="2">
          <span class="invisible">1</span>
        </el-col>
        <el-col :span="17">
          <strong>Delivery Fee</strong>
        </el-col>
        <el-col :span="5" class="centre">
          <strong>
            ${{ order.deliveryAmountDisplay }}
            <br />
            ({{ order.deliveryDistance }})
          </strong>
        </el-col>
      </el-row>
      <el-row v-if="appliedCoupon" class="line-item">
        <el-col :span="2">
          <span class="invisible">1</span>
        </el-col>
        <el-col :span="17">
          <strong>Discount</strong>
          <div>{{ appliedCoupon.description }}</div>
        </el-col>
        <el-col :span="5" class="centre">
          <strong>
            -${{ appliedCoupon.discountAmountDisplay }}
            <span v-if="appliedCoupon.discountPercentageDisplay">
              <br />
              (-{{ appliedCoupon.discountPercentageDisplay }}%)
            </span>
          </strong>
        </el-col>
      </el-row>
      <el-row v-if="paymentSurcharge" class="line-item">
        <el-col :span="2">
          <span class="invisible">1</span>
        </el-col>
        <el-col :span="17">
          <span>
            <strong>Card Payment Surcharge</strong>
          </span>
        </el-col>
        <el-col :span="5" class="centre">
          <strong>
            <span>${{ paymentSurchargeDisplay }}</span>
            <br />
            <span>(1.5%)</span>
          </strong>
        </el-col>
      </el-row>
      <el-row class="line-item">
        <el-col :span="2">
          <span class="invisible">1</span>
        </el-col>
        <el-col :span="17">
          <strong>Total</strong>
        </el-col>
        <el-col :span="5" class="centre">
          <strong>${{ amount }}</strong>
        </el-col>
      </el-row>
    </div>

    <!-- Order detail line items -->
    <div class="line-items" v-if="display.isDetail">
      <el-row class="line-item line-item-header">
        <el-col :span="2">#</el-col>
        <el-col :span="10">
          <strong>Item</strong>
        </el-col>
        <el-col :span="5">
          <strong>Unit Price</strong>
        </el-col>
        <el-col :span="2" class="centre">
          <strong>Qty</strong>
        </el-col>
        <el-col :span="5" class="right">
          <strong>Total</strong>
        </el-col>
      </el-row>
      <div v-for="(item, index) in order.lineItems" :key="item._id">
        <el-row class="line-item">
          <el-col :span="2">{{ index + 1 }}</el-col>
          <el-col :span="10" class="line-item__name">
            <div>
              <span class="line-item__status border-black bg-gold">
                {{
                  LINE_ITEM_STATUS[item.status]
                    ? LINE_ITEM_STATUS[item.status].display
                    : "Unknown"
                }}
              </span>
              {{ item.name }}
              <span v-if="item.specificationSelection">
                ({{ item.specificationSelection.name }})
              </span>
            </div>
          </el-col>
          <el-col :span="5">{{ item.unitNetPriceDisplay }}</el-col>
          <el-col :span="2" class="centre">{{ item.quantity }}</el-col>
          <el-col :span="5" class="right"
            >${{ item.totalGrossPriceDisplay }}</el-col
          >
        </el-row>
        <div v-if="item.packageSelections && item.packageSelections.length > 0">
          <div v-for="(sel, selIdx) in item.packageSelections" :key="selIdx">
            <el-row
              class="line-item item-selections"
              v-for="key in Object.keys(sel)"
              :key="key"
            >
              <el-col :span="2" class="invisible">1</el-col>
              <el-col :span="10" class="line-item__name">
                <p>
                  <strong>{{ key }}</strong>
                </p>
              </el-col>
              <el-col :span="10" class="line-item__name">
                <p>{{ sel[key].join(", ") }}</p>
              </el-col>
            </el-row>
          </div>
        </div>
      </div>
      <el-row class="line-item">
        <el-col :span="2">
          <span class="invisible">1</span>
        </el-col>
        <el-col :span="17">
          <strong>Subtotal</strong>
        </el-col>
        <el-col :span="5" class="centre">
          <strong>${{ subtotal }}</strong>
        </el-col>
      </el-row>
      <el-row v-if="isDelivery(order.mode)" class="line-item">
        <el-col :span="2">
          <span class="invisible">1</span>
        </el-col>
        <el-col :span="17">
          <strong>Delivery Fee</strong>
        </el-col>
        <el-col :span="5" class="centre">
          <strong>${{ order.deliveryAmountDisplay }}</strong>
        </el-col>
      </el-row>
      <div v-if="order.coupons && order.coupons.length > 0">
        <el-row
          v-for="coupon in order.coupons"
          :key="coupon._id"
          class="line-item"
        >
          <el-col :span="2">
            <span class="invisible">1</span>
          </el-col>
          <el-col :span="17">
            <strong>Coupon</strong>
            <div>{{ coupon.description }}</div>
          </el-col>
          <el-col :span="5" class="centre">
            <strong>-${{ coupon.discountAmountDisplay }}</strong>
            <strong v-if="coupon.discountPercentageDisplay">
              <br />(-{{ coupon.discountPercentageDisplay }}%)
            </strong>
          </el-col>
        </el-row>
      </div>
      <el-row v-if="paymentSurcharge" class="line-item">
        <el-col :span="2">
          <span class="invisible">1</span>
        </el-col>
        <el-col :span="17">
          <span>
            <strong>Card Payment Surcharge</strong>
          </span>
        </el-col>
        <el-col :span="5" class="centre">
          <strong>
            <span>${{ paymentSurchargeDisplay }}</span>
            <br />
            <span>(1.5%)</span>
          </strong>
        </el-col>
      </el-row>
      <el-row class="line-item">
        <el-col :span="2">
          <span class="invisible">1</span>
        </el-col>
        <el-col :span="17">
          <strong>Total</strong>
        </el-col>
        <el-col :span="5" class="centre">
          <strong>${{ amount }}</strong>
        </el-col>
      </el-row>
    </div>

    <!-- Order creation/detail form -->
    <div class="order">
      <el-form ref="order" :rules="rules" :model="order">
        <div>
          <h4 class="centre">Contact</h4>
          <el-row>
            <el-col :span="12">
              <el-form-item class="field" prop="customer.firstName">
                <el-input
                  v-model="order.customer.firstName"
                  placeholder="First Name (Required)"
                  :disabled="display.isDetail"
                ></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item class="field" prop="customer.surname">
                <el-input
                  v-model="order.customer.surname"
                  :placeholder="`Surname${
                    isFullCustomerDetailMandatory ? ' (Required)' : ''
                  }`"
                  :disabled="display.isDetail"
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item class="field" prop="customer.phone">
            <el-input
              type="number"
              v-model="order.customer.phone"
              :placeholder="`Mobile Number${
                isFullCustomerDetailMandatory ? ' (Required)' : ''
              }`"
              :disabled="display.isDetail"
            ></el-input>
          </el-form-item>
          <el-form-item class="field" prop="customer.email">
            <el-input
              type="email"
              v-model="order.customer.email"
              placeholder="Email Address"
              :disabled="display.isDetail"
            ></el-input>
          </el-form-item>
        </div>
        <div>
          <h4 class="centre">Comments</h4>
          <el-form-item class="field">
            <el-input
              type="textarea"
              v-model="order.comments"
              placeholder="Comments"
              :disabled="display.isDetail"
            ></el-input>
          </el-form-item>
        </div>
        <div v-if="order.mode !== 'DINE_IN' || !order.tableNumber">
          <h4 class="centre">Order Mode</h4>
          <el-form-item class="field centre order__mode" prop="mode">
            <el-radio-group
              v-model="order.modeDisplay"
              @change="onChangeMode"
              :disabled="display.isDetail || !!selectedModeFromHome"
            >
              <el-radio-button
                v-for="mode in ORDER_MODE"
                :key="mode.value"
                :label="mode.display"
                :disabled="!mode.enabled"
              ></el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-row v-if="order.mode !== 'DINE_IN'">
            <el-col :span="12">
              <el-form-item class="field" prop="expectFulfill.date">
                <el-select
                  class="select"
                  :disabled="
                    display.isDetail ||
                    (order.mode === 'PICK_UP' && order.isPickupAsap)
                  "
                  placeholder="Select date"
                  v-model="order.expectFulfill.date"
                  @change="onChangeExpectedFulfillDate"
                >
                  <el-option
                    v-for="day in expectFulfillSelect.nextAvailableDays"
                    :key="day"
                    :label="day"
                    :value="day"
                    :disabled="day.endsWith('(CLOSED)')"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item class="field" prop="expectFulfill.time">
                <el-select
                  class="select"
                  :disabled="
                    display.isDetail ||
                    (order.mode === 'PICK_UP' && order.isPickupAsap)
                  "
                  placeholder="Select time"
                  v-model="order.expectFulfill.time"
                >
                  <el-option
                    v-for="timeSlot in expectFulfillSelect.TIME_SLOT[
                      order.mode
                    ]"
                    :key="timeSlot"
                    :label="timeSlot"
                    :value="timeSlot"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </div>
        <!-- if dine in event order, only allow selection of event timeslots -->
        <!-- if not, allow selection of reservation timeslots -->
        <el-row
          v-if="
            order.mode === 'DINE_IN' &&
            !order.isDineInAsap &&
            !order.tableNumber
          "
        >
          <el-col :span="24">
            <el-form-item class="field" prop="numberOfCustomers">
              <el-select
                class="full-width"
                v-model="order.numberOfCustomers"
                placeholder="Number of Customers (Required)"
                @change="onChangeNumberOfCustomers"
                :disabled="display.isDetail"
              >
                <el-option
                  v-for="num in newReservation.numberOfCustomersSelection"
                  :key="num"
                  :label="num === 1 ? '1 Customer' : `${num} Customers`"
                  :value="num"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row
          v-if="
            order.mode === 'DINE_IN' &&
            !order.isEventOrder &&
            !order.tableNumber
          "
        >
          <el-col :span="12">
            <el-form-item class="field" prop="expectFulfill.date">
              <el-select
                placeholder="Select date"
                v-model="order.expectFulfill.date"
                @change="onChangeExpectedFulfillDate"
                :disabled="display.isDetail || order.isDineInAsap"
              >
                <el-option
                  v-for="day in expectFulfillSelect.nextAvailableDays"
                  :key="day"
                  :label="day"
                  :value="day"
                  :disabled="day.endsWith('(CLOSED)')"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item class="field" prop="expectFulfill.time">
              <el-select
                placeholder="Select time"
                v-model="order.expectFulfill.time"
                :disabled="display.isDetail || order.isDineInAsap"
              >
                <el-option
                  v-for="timeSlot in expectFulfillSelect.TIME_SLOT[order.mode]"
                  :key="timeSlot"
                  :label="timeSlot"
                  :value="timeSlot"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item
          class="field centre"
          v-if="order.mode === 'DINE_IN' && order.isEventOrder"
          prop="expectFulfillBeforeTimeDisplay"
        >
          <h3>Available Time Slots</h3>
          <el-radio-group
            v-model="order.expectFulfillBeforeTimeDisplay"
            :disabled="display.isDetail"
            @change="onChangeEventSlot"
          >
            <el-radio
              class="order-radio-block"
              v-for="eventSlot in expectFulfillSelect.eventSlots"
              :key="eventSlot.display"
              :label="eventSlot.display"
            ></el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item
          v-if="order.mode === 'PICK_UP'"
          class="field centre"
          prop="isPickupAsap"
        >
          <el-checkbox
            v-model="order.isPickupAsap"
            @change="onChangePickupAsap"
            :disabled="display.isDetail || !isPickupAsapEnabled"
            >Pick up ASAP</el-checkbox
          >
        </el-form-item>
        <el-form-item
          v-if="
            order.mode === 'DINE_IN' &&
            !order.isEventOrder &&
            !order.tableNumber
          "
          class="field centre"
          prop="isDineInAsap"
        >
          <el-checkbox
            v-model="order.isDineInAsap"
            @change="onChangeDineInAsap"
            :disabled="display.isDetail"
            >Now</el-checkbox
          >
        </el-form-item>
        <div v-if="isDelivery(order.mode)">
          <h4 class="centre">Delivery Address</h4>
          <div class="centre" style="margin-bottom: 1rem">
            <delivery-indicator
              v-if="isDelivery(order.mode)"
              :subtotal="Number(subtotal)"
              color="#221714"
              @clickInfo="display.dialog.deliveryRule.isVisible = true"
            ></delivery-indicator>
          </div>
          <el-form-item class="field" prop="shippingAddress.streetAddress">
            <el-input
              v-model="order.shippingAddress.streetAddress"
              placeholder="Street Address (Required)"
              :disabled="display.isDetail"
              @change="isNotMeetMinimumAmountRequirement = false"
            ></el-input>
          </el-form-item>
          <el-row>
            <el-col :span="12">
              <el-form-item class="field" prop="shippingAddress.city">
                <el-input
                  v-model="order.shippingAddress.city"
                  placeholder="Suburb (Required)"
                  :disabled="display.isDetail"
                  @change="isNotMeetMinimumAmountRequirement = false"
                ></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item class="field">
                <el-input
                  v-model="order.shippingAddress.state"
                  disabled
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="12">
              <el-form-item class="field">
                <el-input
                  v-model="order.shippingAddress.country"
                  disabled
                ></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item class="field" prop="shippingAddress.zipCode">
                <el-input
                  type="number"
                  v-model="order.shippingAddress.zipCode"
                  placeholder="Zip Code"
                  :disabled="display.isDetail"
                  @change="isNotMeetMinimumAmountRequirement = false"
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <div
            v-if="isNotMeetMinimumAmountRequirement"
            class="centre red info-not-meet-minimum-amount"
          >
            Delivery distance to current address has a higher minimum order
            amount requirement. Please
            <a
              class="gold"
              @click="display.dialog.deliveryRule.isVisible = true"
            >
              check the delivery rules
            </a>
            or
            <a class="gold" @click="$router.push(`/??hideHomeOptions=true`)">
              add more products </a
            >.
          </div>
        </div>
        <h4 class="centre">Payment</h4>
        <div
          v-if="order.mode === 'DINE_IN' && !order.isEventOrder"
          class="centre"
        >
          <h3 class="gold">
            <strong>Please pay at Reception when your meal is finished.</strong>
          </h3>
        </div>
        <div
          v-if="
            order.mode !== 'DINE_IN' ||
            (order.mode === 'DINE_IN' && order.isEventOrder)
          "
          class="centre"
        >
          <h3 class="gold">
            <strong>Pay ${{ amount }} using</strong>
          </h3>
          <el-form-item class="field" prop="paymentMethod">
            <el-radio-group
              v-model="order.paymentMethodDisplay"
              :disabled="display.isDetail"
              @change="onChangePaymentMethod"
            >
              <el-radio
                class="order-radio-block"
                v-for="method in ORDER_PAYMENT_METHOD.filter((m) => !m.hidden)"
                :key="method.value"
                :label="method.display"
                :disabled="!method.supportedMode.includes(order.mode)"
              ></el-radio>
            </el-radio-group>
          </el-form-item>
        </div>
        <div v-if="order.mode === 'DINE_IN' && display.isDetail">
          <h3 class="centre">Dine In Information</h3>
          <el-row :gutter="20">
            <el-col :span="12" class="right">
              <strong>Queue Number</strong>
            </el-col>
            <el-col :span="12" class="left" v-if="order.queueNumber">
              {{ order.queueNumber }}
            </el-col>
            <el-col :span="12" class="invisible" v-if="!order.queueNumber"
              >x</el-col
            >
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12" class="right">
              <strong>Table Number</strong>
            </el-col>
            <el-col :span="12" class="left" v-if="order.tableNumber">
              {{ order.tableNumber }}
            </el-col>
            <el-col :span="12" class="invisible" v-if="!order.tableNumber"
              >x</el-col
            >
          </el-row>
        </div>
        <div class="centre buttons">
          <el-button class="bg-gold black" @click="onClickBack">Back</el-button>
          <el-button
            class="bg-black gold place-order-btn"
            @click="onClickSubmit"
            v-if="!display.isDetail"
            :disabled="isNotMeetMinimumAmountRequirement"
          >
            Place Order
          </el-button>
        </div>
      </el-form>

      <!-- Order confirmation -->
      <el-dialog
        title="Order Confirmation"
        :visible.sync="display.dialog.orderConfirm.isVisible"
        :width="display.dialogWidth"
      >
        <div>
          <el-row :gutter="20">
            <el-col :span="12" class="right">
              <strong>Customer Name</strong>
            </el-col>
            <el-col :span="12" class="left">
              {{ order.customer.firstName }} /
              {{ order.customer.surname }}
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12" class="right">
              <strong>Phone</strong>
            </el-col>
            <el-col :span="12" class="left">
              {{ order.customer.phone }}
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12" class="right">
              <strong>Email</strong>
            </el-col>
            <el-col :span="12" class="left">
              {{ order.customer.email }}
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12" class="right">
              <strong>Comments</strong>
            </el-col>
            <el-col :span="12" class="left">{{ order.comments }}</el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12" class="right">
              <strong>Mode</strong>
            </el-col>
            <el-col :span="12" class="left">{{ order.modeDisplay }}</el-col>
          </el-row>
          <el-row :gutter="20" v-if="order.queueNumber">
            <el-col :span="12" class="right">
              <strong>Queue Number</strong>
            </el-col>
            <el-col :span="12" class="left">{{ order.queueNumber }}</el-col>
          </el-row>
          <el-row :gutter="20" v-if="order.tableNumber">
            <el-col :span="12" class="right">
              <strong>Table Number</strong>
            </el-col>
            <el-col :span="12" class="left">{{ order.tableNumber }}</el-col>
          </el-row>

          <el-row :gutter="20" v-if="order.mode !== 'DINE_IN'">
            <el-col :span="12" class="right">
              <strong>{{ order.modeDisplay }} Time</strong>
            </el-col>
            <el-col :span="12" class="left">
              <span v-if="!(order.mode === 'PICK_UP' && order.isPickupAsap)"
                >{{ order.expectFulfill.time }}
                {{ order.expectFulfill.date }}</span
              >
              <br v-if="!(order.mode === 'PICK_UP' && order.isPickupAsap)" />
              {{
                order.isPickupAsap
                  ? "ASAP"
                  : `(No later than ${order.expectFulfillBeforeTimeDisplay})`
              }}
            </el-col>
          </el-row>
          <div v-if="isDelivery(order.mode)">
            <el-row :gutter="20">
              <el-col :span="12" class="right">
                <strong>Address</strong>
              </el-col>
              <el-col :span="12" class="left">
                {{ order.shippingAddress.streetAddress }},
                {{ order.shippingAddress.city }},
                {{ order.shippingAddress.state }},
                {{ order.shippingAddress.zipCode }}
              </el-col>
            </el-row>
          </div>
          <el-row :gutter="20">
            <el-col :span="12" class="right">
              <strong>Payment</strong>
            </el-col>
            <el-col :span="12" class="left">
              {{ order.paymentMethodDisplay }}
            </el-col>
          </el-row>
          <h3>Line Items</h3>
          <el-row class="line-item line-item-header">
            <el-col :span="2">
              <strong>#</strong>
            </el-col>
            <el-col :span="9">
              <strong>Item</strong>
            </el-col>
            <el-col :span="5" class="centre">
              <strong>Unit Price</strong>
            </el-col>
            <el-col :span="3" class="centre">
              <strong>Qty</strong>
            </el-col>
            <el-col :span="5" class="centre">
              <strong>Total</strong>
            </el-col>
          </el-row>
          <el-row
            class="line-item"
            v-for="(item, index) in shoppingCart.shoppingCart.lineItems"
            :key="item._id"
          >
            <el-col :span="2">{{ index + 1 }}</el-col>
            <el-col :span="9">
              <div>
                {{ item.name }}
                <span v-if="item.specificationSelection">
                  ({{ item.specificationSelection.name }})
                </span>
              </div>
              <div
                v-for="(sel, selIdx) in item.packageSelections"
                :key="selIdx"
              >
                <div v-for="key in Object.keys(sel)" :key="key">
                  <p>
                    <strong>{{ key }}</strong>
                  </p>
                  <p>{{ sel[key].join(", ") }}</p>
                </div>
              </div>
            </el-col>
            <el-col :span="5" class="centre">
              {{ item.unitNetPriceDisplay }}
            </el-col>
            <el-col :span="3" class="centre">{{ item.quantity }}</el-col>
            <el-col :span="5" class="centre"
              >${{ item.totalGrossPriceDisplay }}</el-col
            >
          </el-row>
          <el-row class="line-item">
            <el-col :span="2">
              <span class="invisible">1</span>
            </el-col>
            <el-col :span="17">
              <strong>Subtotal</strong>
            </el-col>
            <el-col :span="5" class="centre">
              <strong>${{ subtotal }}</strong>
            </el-col>
          </el-row>
          <el-row v-if="isDelivery(order.mode)" class="line-item">
            <el-col :span="2">
              <span class="invisible">1</span>
            </el-col>
            <el-col :span="17">
              <strong>Delivery Fee</strong>
            </el-col>
            <el-col :span="5" class="centre">
              <strong>
                ${{ order.deliveryAmountDisplay }}
                <br />
                ({{ order.deliveryDistance }})
              </strong>
            </el-col>
          </el-row>
          <el-row v-if="appliedCoupon" class="line-item">
            <el-col :span="2">
              <span class="invisible">1</span>
            </el-col>
            <el-col :span="17">
              <strong>Coupon</strong>
              <div>{{ appliedCoupon.description }}</div>
            </el-col>
            <el-col :span="5" class="centre">
              <strong>
                -${{ appliedCoupon.discountAmountDisplay }}
                <span v-if="appliedCoupon.discountPercentageDisplay">
                  <br />
                  (-{{ appliedCoupon.discountPercentageDisplay }}%)
                </span>
              </strong>
            </el-col>
          </el-row>
          <el-row v-if="paymentSurcharge" class="line-item">
            <el-col :span="2">
              <span class="invisible">1</span>
            </el-col>
            <el-col :span="17">
              <span>
                <strong>Card Payment Surcharge</strong>
              </span>
            </el-col>
            <el-col :span="5" class="centre">
              <strong>
                <span>${{ paymentSurchargeDisplay }}</span>
                <br />
                <span>(1.5%)</span>
              </strong>
            </el-col>
          </el-row>
          <el-row class="line-item">
            <el-col :span="2">
              <span class="invisible">1</span>
            </el-col>
            <el-col :span="17">
              <strong>Total</strong>
            </el-col>
            <el-col :span="5" class="centre">
              <strong>${{ amount }}</strong>
            </el-col>
          </el-row>
        </div>
        <div slot="footer" class="centre">
          <el-button class="bg-black gold" @click="onClickOrderConfirm"
            >Confirm</el-button
          >
        </div>
      </el-dialog>

      <!-- Dine In Table/Queue -->
      <el-dialog
        :visible.sync="display.dialog.queue.isVisible"
        width="80%"
        title="Dine In"
      >
        <div class="break-word">
          <div v-if="queues.length <= 0">
            <h3 class="centre">More Information Required</h3>
            <p>
              We did not find any queue or table number under your phone number.
            </p>
            <p>
              You can choose to either provide your current table number or to
              queue for dining in.
            </p>
            <h3 class="centre">Your Table Number</h3>
            <el-input
              v-model="order.tableNumber"
              placeholder="Your assigned table number"
            ></el-input>
            <div class="queue__section centre">
              <el-button
                class="black bg-gold"
                @click="onClickUseDineInTableNumber"
                >Use Table Number</el-button
              >
            </div>
            <p class="centre">Or if you haven't got seated</p>
            <h3 class="centre">Queue For Dine-In</h3>
            <div>
              <el-input v-model="newQueue.name" placeholder="Name"></el-input>
            </div>
            <div class="queue__section">
              <el-input
                v-model="newQueue.phone"
                type="number"
                placeholder="Phone"
              ></el-input>
            </div>
            <div class="queue__section">
              <el-input
                v-model="newQueue.numberOfCustomers"
                type="number"
                placeholder="Number of customers"
              ></el-input>
            </div>
            <div class="queue__section centre">
              <el-button class="black bg-gold" @click="onClickSubmitQueue"
                >Submit Queue</el-button
              >
            </div>
          </div>
          <div v-if="queues.length > 0">
            <h3 class="centre">You Are Queued For Dine In.</h3>
            <p class="justify">
              Based on the phone number provided, we've found the following
              queue information.
            </p>
            <el-row :gutter="20">
              <el-col :span="12" class="right font-bold">Name</el-col>
              <el-col :span="12">{{
                queues[0].firstName
                  ? `${queues[0].firstName} ${queues[0].surname}`
                  : queues[0].name
              }}</el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="12" class="right font-bold">Phone</el-col>
              <el-col :span="12">{{ queues[0].phone }}</el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="12" class="right font-bold"
                >Number of Customers</el-col
              >
              <el-col :span="12">{{ queues[0].numberOfCustomers }}</el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="12" class="right font-bold">Queue Number</el-col>
              <el-col :span="12">{{ queues[0].queueNumber }}</el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="12" class="right font-bold">Table Number</el-col>
              <el-col :span="12">{{ queues[0].tableNumber }}</el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="12" class="right font-bold">Status</el-col>
              <el-col :span="12">{{ queues[0].status }}</el-col>
            </el-row>
            <div class="centre queue__section">
              <el-button
                class="black bg-gold"
                @click="onClickUseDineInQueueTableInfo"
                >Use Queue/Table Information</el-button
              >
            </div>
            <div class="queue__section">
              <p class="justify">
                Queue/Table number incorrect? Please provide below.
              </p>
            </div>
            <div class="queue__section">
              <el-input
                v-model="queues[0].queueNumber"
                type="number"
                placeholder="Queue Number"
              ></el-input>
            </div>
            <div class="queue__section">
              <el-input
                v-model="queues[0].tableNumber"
                placeholder="Table Number"
              ></el-input>
            </div>
            <div class="queue__section centre">
              <el-button
                class="black bg-gold"
                @click="onClickUseDineInQueueTableInfo"
                >Submit Information</el-button
              >
            </div>
          </div>
        </div>
      </el-dialog>

      <!-- Dine In reservation dialog -->
      <el-dialog
        :visible.sync="display.dialog.reservation.isVisible"
        width="80%"
        title="Dine In"
      >
        <div class="break-word justify field">
          <h3 class="centre">Please select your reservation</h3>
          <p>
            We found the following reservations by the mobile number provided.
            Please kindly select the one you would like to use.
          </p>
          <el-select
            class="full-width"
            v-model="selectedReservation"
            placeholder="Please select reservation"
          >
            <el-option
              v-for="reservation in reservations"
              :key="reservation._id"
              :label="reservation.label"
              :value="reservation"
            ></el-option>
          </el-select>
        </div>
        <div slot="footer" class="centre">
          <el-button class="bg-black gold" @click="onClickUseReservation"
            >Confirm</el-button
          >
        </div>
      </el-dialog>
    </div>

    <!-- Delivery rule dialog -->
    <delivery-rule-dialog
      :isVisible="display.dialog.deliveryRule.isVisible"
      @close="display.dialog.deliveryRule.isVisible = false"
    ></delivery-rule-dialog>

    <!-- Error dialog -->
    <el-dialog
      width="80%"
      :title="display.dialog.error.title"
      :visible.sync="display.dialog.error.isVisible"
      :before-close="onClickAcknowledgeError"
    >
      <div class="centre">
        <p>{{ display.dialog.error.message }}</p>
        <div>
          <el-button class="bg-black gold" @click="onClickAcknowledgeError"
            >OK</el-button
          >
        </div>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import Cookies from "js-cookie";
import { mapGetters } from "vuex";
import { isNil, isEmpty, or, indexBy, prop } from "ramda";
import moment from "moment";
import { create, getById } from "@/api/order";
import { getMe } from "@/api/customer";
import { getPublic, getMine, searchCoupons } from "@/api/coupon";
import { calculateDeliveryFee } from "@/api/fee";
import {
  getTodayQueuesByPhone,
  createQueue,
  getQueueById,
} from "@/api/dine-in";
import {
  getTodayReservationByPhone,
  getReservationRulesByDate,
  create as createReservation,
  getById as getReservationById,
  linkOrder,
} from "@/api/reservation";
import { listGeneral } from "@/api/setting";
import logger from "@/utils/logger";
import {
  calculateSubtotal,
  getAttributeValue,
  setAttributeValue,
  isNotNilOrEmpty,
  deepClone,
  isAlipayBrowser,
  featureToggle,
  isPickupAsapEnabled,
  CONFIG_KEYS,
} from "@/utils";
import {
  ORDER_MODE,
  ORDER_STATUS,
  ORDER_PAYMENT_METHOD,
  TIME_SLOT,
  LINE_ITEM_STATUS,
  calculateSurcharge,
  calculateTotalAmount,
  isDelivery,
  isDineIn,
  MINIMUM_DELIVERY_AMOUNT_MATRIX,
  hasEventProduct,
  getEventProduct,
  getOrderAvailableDates,
} from "@/utils/order";
import { errorMessages } from "@/utils/info";
import { decorateShoppingCartItem } from "@/utils/shoppingCart";
import { decorateCoupon } from "@/utils/coupon";
import {
  getReservationDates,
  getReservationTimeslots,
  getNumberOfCustomerSelections,
} from "@/utils/dine-in";

const name = "Order";
const log = logger(name);

const ERROR_STATUS_WITH_MESSAGE = {
  412: "Order has expired.",
  403: "Please sign in to view this order.",
};

export default {
  name,
  data() {
    const isMdAndUp = window.innerWidth >= 992;
    return {
      featureToggle,

      selectedModeFromHome: Cookies.get(
        "kingdynasty_ec_home_options_selected_mode"
      ),
      availableCoupons: [],
      appliedCoupon: null,
      order: {
        customer: {},
        shippingAddress: {},
        billingAddress: {},
        attributes: [
          {
            key: "mode",
            value: ORDER_MODE.find((m) => m.value === "PICK_UP").value,
          },
          {
            key: "paymentMethod",
            value: ORDER_PAYMENT_METHOD.find((m) => m.value === "EFT").value,
          },
        ],
        mode: ORDER_MODE.find((m) => m.value === "PICK_UP").value,
        modeDisplay: ORDER_MODE.find((m) => m.value === "PICK_UP").display,
        paymentMethod: ORDER_PAYMENT_METHOD.find((m) => (m.value = "EFT"))
          .value,
        paymentMethodDisplay: ORDER_PAYMENT_METHOD.find(
          (m) => m.value === "EFT"
        ).display,
        expectFulfill: {
          date: "",
          time: "",
        },
        expectFulfillBeforeTimeDisplay: "",
        couponIds: [],
        coupons: [],
        deliveryAmount: 0,
        deliveryAmountDisplay: "0.00",
        deliveryDistance: "0m",
        tableNumber: "",
        queueNumber: null,
        isPickupAsap: false,
        isDineInAsap: true,
        isEventOrder: false,
        numberOfCustomers: 10,
      },

      subtotal: "0.00",
      amount: "0.00",
      paymentSurcharge: 0,
      paymentSurchargeDisplay: "0.00",

      isDelivery,
      isNotMeetMinimumAmountRequirement: false,

      queues: [],
      newQueue: {},

      reservations: [],
      selectedReservation: null,
      newReservation: {
        attributes: {
          numberOfCustomers: 10,
        },
        numberOfCustomersSelection: getNumberOfCustomerSelections(1),
      },

      display: {
        isLoading: true,
        title: "Place Order",
        isDetail: false,
        isFromPaymentResult: false,
        dialog: {
          orderConfirm: { isVisible: false },
          queue: { isVisible: false },
          reservation: { isVisible: false },
          deliveryRule: { isVisible: false },
          error: {
            isVisible: false,
            title: "Cannot View Order",
            message: "",
          },
        },
        dialogWidth: isMdAndUp ? "60%" : "90%",
      },

      ORDER_MODE,
      status: ORDER_STATUS.reduce(
        (current, incoming) =>
          Object.assign(current, { [incoming.value]: incoming }),
        {}
      ),
      ORDER_PAYMENT_METHOD: isAlipayBrowser()
        ? ORDER_PAYMENT_METHOD.filter((method) => method.value === "ALIPAY")
        : ORDER_PAYMENT_METHOD,
      expectFulfillSelect: {
        TIME_SLOT: deepClone(TIME_SLOT),
        nextAvailableDays: getOrderAvailableDates(10),
        eventSlots: [],
      },
      LINE_ITEM_STATUS,

      generalSettings: [],
      isPickupAsapEnabled: true,
      isFullCustomerDetailMandatory: true,
    };
  },
  computed: {
    ...mapGetters(["shoppingCartId", "shoppingCart", "token"]),
    rules() {
      return {
        "customer.firstName": [
          {
            required: true,
            message: "Please type first name.",
            trigger: "blur",
          },
        ],
        "customer.surname": [
          {
            required: this.isFullCustomerDetailMandatory,
            message: "Please type surname.",
            trigger: "blur",
          },
        ],
        "customer.phone": [
          {
            required: this.isFullCustomerDetailMandatory,
            message: "Please type mobile number.",
            trigger: "blur",
          },
          {
            min: 10,
            max: 10,
            message:
              "Please type a valid Australian mobile number (10 digits).",
            trigger: "blur",
          },
        ],
        "customer.email": [
          {
            type: "email",
            message: "Please type a valid email address.",
            trigger: "blur",
          },
        ],
        "expectFulfill.date": [
          {
            required:
              (this.order.mode === "PICK_UP" && !this.order.isPickupAsap) ||
              (this.order.mode === "DINE_IN" &&
                !this.order.isDineInAsap &&
                !this.order.isEventOrder) ||
              this.order.mode === "TAKEAWAY",
            message: "Please select a date for pickup/delivery",
            trigger: "change",
          },
        ],
        "expectFulfill.time": [
          {
            required:
              (this.order.mode === "PICK_UP" && !this.order.isPickupAsap) ||
              (this.order.mode === "DINE_IN" && !this.order.isDineInAsap) ||
              this.order.mode === "TAKEAWAY",
            message: "Please select a time slot for pickup/delivery",
            trigger: "change",
          },
        ],
        "shippingAddress.streetAddress": [
          {
            required: true,
            message: "Please type street address.",
            trigger: "blur",
          },
        ],
        "shippingAddress.city": [
          { required: true, message: "Please type suburb.", trigger: "blur" },
        ],
        "shippingAddress.zipCode": [
          {
            min: 4,
            max: 4,
            message: "Please type a valid Australian zip code (4 digits).",
            trigger: "blur",
          },
        ],
        numberOfCustomers: [
          {
            required: isDineIn(this.order.mode) && !this.order.isDineInAsap,
            message: "Please select number of customers",
            trigger: "blur",
          },
        ],
      };
    },
  },
  async created() {
    log.info("Created");
    if (isAlipayBrowser()) {
      const alipayMethod = this.ORDER_PAYMENT_METHOD.find(
        (m) => m.value === "ALIPAY"
      );
      this.order.paymentMethod = alipayMethod.value;
      this.order.paymentMethodDisplay = alipayMethod.display;
      setAttributeValue(
        this.order.attributes,
        "paymentMethod",
        alipayMethod.value
      );
    }

    // order detail
    if (this.$route.params.id) {
      if (this.$route.query.isFromPaymentResult) {
        this.display.isFromPaymentResult =
          this.$route.query.isFromPaymentResult === "true";
      } else if (this.$route.query && this.$route.query.transactionId) {
        const paymentResultQuery = this.$route.query.transactionId.substring(
          this.$route.query.transactionId.indexOf("isFromPaymentResult")
        );
        this.display.isFromPaymentResult =
          paymentResultQuery === "isFromPaymentResult=true";
      }
      try {
        await this._loadOrder();
        await this._loadOrderCoupons();
      } catch (err) {
        log.error("Load order error", err);
        if (err.response && err.response.status) {
          this.display.dialog.error.message =
            ERROR_STATUS_WITH_MESSAGE[err.response.status] || "System error.";
          this.display.dialog.error.isVisible = true;
        }
        return;
      } finally {
        this.display.isLoading = false;
      }
      return;
    }

    // create order
    await this._loadOrderCreation();
    if (this.selectedModeFromHome) {
      log.info("Home page selected mode", this.selectedModeFromHome);
      this.order.modeDisplay = ORDER_MODE.find(
        (m) => m.value === this.selectedModeFromHome
      ).display;
      await this.onChangeMode(this.order.modeDisplay);
    }
    if (this.order.mode === "DINE_IN") {
      if (!this.order.isEventOrder) {
        this.expectFulfillSelect.nextAvailableDays = getReservationDates(21);
      } else {
        this.expectFulfillSelect.eventSlots = getAttributeValue(
          getEventProduct(this.shoppingCart.shoppingCart.lineItems).attributes,
          "availableTimeslots"
        ).map((eventSlot) => ({
          display: moment(eventSlot).local().format("hh:mma, D MMM YYYY"),
          value: new Date(eventSlot),
        }));
        this.order.expectFulfillBeforeTimeDisplay =
          this.expectFulfillSelect.eventSlots[0].display;
        this.order.expectFulfillBeforeTime =
          this.expectFulfillSelect.eventSlots[0].value;
      }
    }
    await this._loadAvailableCoupons();
    if (isNotNilOrEmpty(this.availableCoupons)) {
      this.appliedCoupon = this.availableCoupons[0];
      this.order.couponIds = [this.appliedCoupon._id];
    }
    this._calculateAmounts();
    if (this.amount <= 0) {
      log.info(
        `Order amount is ${this.amount} after discount, not to apply coupon`
      );
      this.amount = Number(this.subtotal).toFixed(2);
      this.appliedCoupon = null;
      this.order.couponIds = [];
    }
    await this.fetchGeneralSettings();
    this.prepareShopClosure();
    log.info("Next available days", this.expectFulfillSelect.nextAvailableDays);
    if (localStorage.getItem(CONFIG_KEYS.reservationId)) {
      await this._loadReservation();
    }
    this.order.tableNumber = Cookies.get(
      "kingdynasty_ec_home_options_table_number"
    );
    this.isFullCustomerDetailMandatory =
      this.order.mode !== "DINE_IN" || !this.order.tableNumber;
    this.display.isLoading = false;
  },
  methods: {
    onClickAcknowledgeError() {
      this.$router.replace("/");
    },

    onChangeNumberOfCustomers(event) {
      this.newReservation.attributes.numberOfCustomers = Number(event);
    },

    async fetchGeneralSettings() {
      try {
        this.generalSettings = await listGeneral();
      } catch (err) {
        log.error("Error fetching general settings", err);
      }
    },
    prepareShopClosure() {
      const isClosed = this.generalSettings.find(
        (setting) => setting.slug === "shop-close"
      );
      const closeDate = this.generalSettings.find(
        (setting) => setting.slug === "shop-close-date"
      );
      if (!isClosed || or(isNil(isClosed.details), isEmpty(isClosed.details))) {
        log.info("Shop is not closed");
        return;
      }
      if (!closeDate || !closeDate.details) {
        log.info("Shop is closed but close date not specified");
        return;
      }
      this.isPickupAsapEnabled = isPickupAsapEnabled;
      const closeDatesFormatted = closeDate.details.map((date) =>
        moment(date).local().format("ddd D MMM YYYY")
      );
      this.expectFulfillSelect.nextAvailableDays.forEach((date, dateIndex) => {
        if (closeDatesFormatted.includes(date)) {
          this.expectFulfillSelect.nextAvailableDays[
            dateIndex
          ] = `${date} (CLOSED)`;
        }
      });
    },

    onChangePickupAsap(event) {
      if (event) {
        this.order.expectFulfillBeforeTime = null;
        this.order.expectFulfill = {
          date: "",
          time: "",
        };
      }
    },

    onChangeDineInAsap(event) {
      if (event) {
        this.order.expectFulfillBeforeTime = null;
        this.order.expectFulfill = {
          date: "",
          time: "",
        };
      }
    },

    async onChangeMode(event) {
      this.display.isLoading = true;
      try {
        log.info("Mode changed to", event);
        this.order.deliveryAmount = 0;
        this.order.deliveryAmountDisplay = "0.00";
        this.order.deliveryDistance = "0m";
        const chosenOrderMode = ORDER_MODE.find(
          (mode) => mode.display === event
        );
        const defaultPaymentMethod = isAlipayBrowser()
          ? ORDER_PAYMENT_METHOD.find((m) => m.value === "ALIPAY")
          : ORDER_PAYMENT_METHOD.find((m) => m.value === "EFT");
        const cashPaymentMethod = ORDER_PAYMENT_METHOD.find(
          (m) => m.value === "CASH_OR_CARD"
        );
        setAttributeValue(this.order.attributes, "mode", chosenOrderMode.value);
        setAttributeValue(this.order.attributes, "deliveryDistance", 0);
        this.order.mode = chosenOrderMode.value;
        this.order.expectFulfill = { date: "", time: "" };
        if (chosenOrderMode.value === "TAKEAWAY") {
          // Delivery order
          this.order.shippingAddress = {
            state: "NSW",
            country: "Australia",
          };
          this.order.paymentMethod =
            this.order.paymentMethodDisplay === cashPaymentMethod.display
              ? defaultPaymentMethod.value
              : this.order.paymentMethod;
          this.order.paymentMethodDisplay =
            this.order.paymentMethodDisplay === cashPaymentMethod.display
              ? defaultPaymentMethod.display
              : this.order.paymentMethodDisplay;
          this.order.shippingAddress = (this.order.customer &&
            this.order.customer.shippingAddress) || {
            state: "NSW",
            country: "Australia",
          };
          await this._fetchOrderDeliveryFee();
        } else {
          this.order.shippingAddress = {};
          if (chosenOrderMode.value === "DINE_IN" && !this.order.isEventOrder) {
            this.order.paymentMethod = ORDER_PAYMENT_METHOD.find(
              (m) => m.value === "PAY_AT_COUNTER"
            );
          } else {
            // Takeaway order
            // if (this.order.paymentMethod.value === "PAY_AT_COUNTER") {
            this.order.paymentMethod = defaultPaymentMethod;
            // }
          }
          this.order.paymentMethodDisplay = this.order.paymentMethod.display;
        }
        await this._loadAvailableCoupons();
        this._applyCouponByMode(this.order.mode);
        this.onChangePaymentMethod(this.order.paymentMethodDisplay);
      } finally {
        this.display.isLoading = false;
      }
    },
    _applyCouponByMode(mode) {
      if (mode === "DINE_IN" || !isNotNilOrEmpty(this.availableCoupons)) {
        this.appliedCoupon = null;
        this.order.couponIds = [];
      } else {
        this.appliedCoupon = this.availableCoupons[0];
        this.order.couponIds = [this.appliedCoupon._id];
      }
    },

    onChangeEventSlot(event) {
      log.info("Event timeslot changed to", event);
      this.order.expectFulfillBeforeTimeDisplay = event;
      this.order.expectFulfillBeforeTime =
        this.expectFulfillSelect.eventSlots.find(
          (eventSlot) => eventSlot.display === event
        ).value;
    },

    onChangePaymentMethod(event) {
      log.info("Payment method changed to", event);
      const chosenPaymentMethod = ORDER_PAYMENT_METHOD.find(
        (m) => m.display === event
      );
      this.order.paymentMethod = chosenPaymentMethod;
      setAttributeValue(
        this.order.attributes,
        "paymentMethod",
        chosenPaymentMethod.value
      );
      this._calculateAmounts();
    },

    async onChangeExpectedFulfillDate(event) {
      const selectedDateMoment = moment(event, "D MMM YYYY");
      if (isDineIn(this.order.mode)) {
        log.info(
          "Future dine in, updating dine-in time slots same as reservation"
        );
        const reservationRules = await getReservationRulesByDate(
          selectedDateMoment.format("YYYY-MM-DD")
        );
        const reservationTimeslots = getReservationTimeslots(
          selectedDateMoment,
          reservationRules
        );
        this.expectFulfillSelect.TIME_SLOT.dineInSlotMap = indexBy(
          prop("display"),
          reservationTimeslots
        );
        this.expectFulfillSelect.TIME_SLOT.DINE_IN = reservationTimeslots.map(
          (timeslot) => timeslot.display
        );
        this._initNumberOfCustomerSelections(reservationRules);
      } else if (!isDelivery(this.order.mode)) {
        log.info("Not delivery, updating pickup time slots available as usual");
        this.expectFulfillSelect.TIME_SLOT.PICK_UP = TIME_SLOT.PICK_UP;
      } else {
        const weekday = selectedDateMoment.weekday();
        if ([6, 0].includes(weekday)) {
          log.info("Delivery on Sat/Sun, change time slot available");
          this.expectFulfillSelect.TIME_SLOT.TAKEAWAY =
            TIME_SLOT.DELIVERY_WEEKEND;
        } else {
          log.info("Delivery not on Sat/Sun, resume normal time slot");
          this.expectFulfillSelect.TIME_SLOT.TAKEAWAY = TIME_SLOT.TAKEAWAY;
        }
      }
      if (selectedDateMoment.isSame(moment(), "day")) {
        log.info("Selected same day order");
        const filterSlotsMinutesAfter = (slots, minutes) =>
          slots.filter((slot) =>
            moment(slot.split("-")[0], "h:mma").isAfter(
              moment().add(minutes, "m")
            )
          );
        this.expectFulfillSelect.TIME_SLOT.PICK_UP = filterSlotsMinutesAfter(
          this.expectFulfillSelect.TIME_SLOT.PICK_UP,
          15
        );
        this.expectFulfillSelect.TIME_SLOT.TAKEAWAY = filterSlotsMinutesAfter(
          this.expectFulfillSelect.TIME_SLOT.TAKEAWAY,
          45
        );
        this.expectFulfillSelect.TIME_SLOT.DELIVERY_WEEKEND =
          filterSlotsMinutesAfter(
            this.expectFulfillSelect.TIME_SLOT.DELIVERY_WEEKEND,
            45
          );
      }

      this.order.expectFulfill.time = null;
    },

    onClickSubmit() {
      this.display.isLoading = true;
      this.$refs["order"].validate(async (valid) => {
        if (!valid) {
          log.warn("Order form invalid");
          this.display.isLoading = false;
          return false;
        }
        if (this.order.mode === "DINE_IN") {
          this.order.tableNumber = Cookies.get(
            "kingdynasty_ec_home_options_table_number"
          );
          if (this.order.isEventOrder || !this.order.isDineInAsap) {
            this.display.dialog.orderConfirm.isVisible = true;
          } else if (this.order.tableNumber) {
            log.info("Dine-in table number prefilled");
            setAttributeValue(
              this.order.attributes,
              "tableNumber",
              this.order.tableNumber
            );
          } else {
            const reservations = (
              await getTodayReservationByPhone(this.order.customer.phone)
            ).filter((reservation) => reservation.status !== "CANCELLED");
            if (!isEmpty(reservations)) {
              log.info(
                `Reservations today exist, count=${reservations.length}`
              );
              reservations.forEach((reservation) => {
                const reservedTimeMoment = moment(reservation.reservedTime);
                reservation.label = [
                  reservedTimeMoment.format("hh:mma, D MMM YYYY"),
                  reservation.customer.firstName,
                  `${reservation.attributes.numberOfCustomers} customers`,
                  reservation.attributes.tableNumber
                    ? `Table ${reservation.attributes.tableNumber}`
                    : null,
                ]
                  .filter((field) => !!field)
                  .join(", ");
              });
              reservations.sort((r1, r2) => r1.reservedTime > r2.reservedTime);
              this.reservations = reservations;
              this.display.dialog.reservation.isVisible = true;
              this.display.isLoading = false;
              return;
            }
            const queues = await getTodayQueuesByPhone(
              this.order.customer.phone
            );
            this.queues = queues;
            this.display.dialog.queue.isVisible = true;
            if (isEmpty(queues)) {
              const { firstName, surname, phone } = this.order.customer;
              this.newQueue.firstName = firstName;
              this.newQueue.surname = surname;
              this.newQueue.phone = phone;
            }
            log.info(`Queues today exist, count=${queues.length}`);
            this.display.isLoading = false;
            return;
          }
        }
        // Order amount limit for delivery
        if (isDelivery(this.order.mode) && Number(this.subtotal) < 100) {
          this.display.isLoading = false;
          return this.$message(errorMessages.LESS_THAN_MINIMUM_DELIVERY_AMOUNT);
        }
        try {
          await this._prepareOrderCreationData();
        } catch (err) {
          this.isNotMeetMinimumAmountRequirement = true;
          return;
        } finally {
          this.display.isLoading = false;
        }
        if (this._isExpectDateBeforeNow()) {
          log.warn("Order fulfill time is earlier than now");
          this.$message({
            message: "Serve time cannot be before now",
            type: "error",
          });
          this.display.isLoading = false;
          return false;
        }
        if (
          this.featureToggle.DISALLOW_SAME_DAY_WEEKEND_DELIVERY &&
          this._isSameDayWeekendDelivery()
        ) {
          // Weekend delivery order happening on the same day
          this.$message({
            message:
              "Please place your weekend delivery order at least 1 day in advance.",
            type: "error",
          });
          this.display.isLoading = false;
          return false;
        }
        this.display.dialog.orderConfirm.isVisible = true;
        this.display.isLoading = false;
      });
    },
    _isExpectDateBeforeNow() {
      if (this.order.isDineInAsap || this.order.isPickupAsap) {
        return false;
      }
      return moment(this.order.expectFulfillBeforeTime).isBefore(moment());
    },
    _isSameDayWeekendDelivery() {
      return (
        isDelivery(this.order.mode) &&
        moment().isSame(this.order.expectFulfillBeforeTime, "day") &&
        [6, 0].includes(moment().weekday())
      );
    },
    async _fetchOrderDeliveryFee() {
      if (
        !this.order.shippingAddress ||
        !this.order.shippingAddress.streetAddress ||
        !this.order.shippingAddress.city
      ) {
        this.order.deliveryAmount = 0;
        this.order.deliveryAmountDisplay = "0.00";
        this.order.deliveryDistance = "0m";
        setAttributeValue(this.order.attributes, "deliveryDistance", 0);
        return;
      }
      const amount = Number(this.subtotal);
      const { deliveryAmount, distance } = await calculateDeliveryFee({
        ...this.order,
        amount,
      });
      this.order.deliveryAmount = deliveryAmount;
      this.order.deliveryAmountDisplay = deliveryAmount.toFixed(2);
      this.order.deliveryDistance =
        distance < 1000 ? `${distance}m` : `${(distance / 1000).toFixed(1)}km`;
      setAttributeValue(this.order.attributes, "deliveryDistance", distance);
    },
    async _prepareOrderCreationData() {
      if (isDineIn(this.order.mode)) {
        if (this.order.isDineInAsap) {
          this.order.expectFulfillBeforeTime = new Date();
        }
      } else {
        this.order.expectFulfillBeforeTime = moment(
          `${this.order.expectFulfill.time.split("-")[1]} ${
            this.order.expectFulfill.date
          }`,
          "HH:mma D MMM YYYY"
        ).toDate();
      }
      this.order.expectFulfillBeforeTimeDisplay = moment(
        this.order.expectFulfillBeforeTime
      ).format("HH:mm D MMM YYYY");
      log.info("Order form valid, submitting", this.order);
      if (isDelivery(this.order.mode)) {
        await this._fetchOrderDeliveryFee();
        const distance = getAttributeValue(
          this.order.attributes,
          "deliveryDistance",
          0
        );
        const matchedRule = MINIMUM_DELIVERY_AMOUNT_MATRIX.find(
          (rule) =>
            distance >= rule.distance.min && distance <= rule.distance.max
        );
        if (this.subtotal < matchedRule.minimumAmount) {
          log.info(
            `Subtotal not reaching minimum amount, subtotal=${this.subtotal}, minimumAmount=${matchedRule.minimumAmount}`
          );
          this.$message(
            errorMessages.LESS_THAN_MINIMUM_DELIVERY_AMOUNT(
              matchedRule.minimumAmount
            )
          );
          throw new Error("Subtotal not reaching minimum amount");
        }
      } else {
        setAttributeValue(this.order.attributes, "deliveryDistance", 0);
      }
      this._calculateAmounts();
      this.order.tableNumber = getAttributeValue(
        this.order.attributes,
        "tableNumber"
      );
      this.order.queueNumber = getAttributeValue(
        this.order.attributes,
        "queueNumber"
      );
    },
    async onClickUseDineInTableNumber() {
      this.display.isLoading = true;
      setAttributeValue(
        this.order.attributes,
        "tableNumber",
        this.order.tableNumber
      );
      await this._prepareOrderCreationData();
      this.display.dialog.queue.isVisible = false;
      this.display.dialog.orderConfirm.isVisible = true;
      this.display.isLoading = false;
    },
    async onClickSubmitQueue() {
      this.display.isLoading = true;
      const { _id, queueNumber } = await createQueue(this.newQueue);
      setAttributeValue(this.order.attributes, "queueId", _id);
      setAttributeValue(this.order.attributes, "queueNumber", queueNumber);
      await this._prepareOrderCreationData();
      this.display.dialog.queue.isVisible = false;
      this.display.dialog.orderConfirm.isVisible = true;
      this.display.isLoading = false;
    },
    async onClickUseDineInQueueTableInfo() {
      this.display.isLoading = true;
      const { _id, queueNumber, tableNumber } = this.queues[0];
      setAttributeValue(this.order.attributes, "queueId", _id);
      setAttributeValue(this.order.attributes, "queueNumber", queueNumber);
      setAttributeValue(this.order.attributes, "tableNumber", tableNumber);
      await this._prepareOrderCreationData();
      this.display.dialog.queue.isVisible = false;
      this.display.dialog.orderConfirm.isVisible = true;
      this.display.isLoading = false;
    },
    async onClickUseReservation() {
      this.display.isLoading = true;
      const {
        _id,
        attributes: { tableNumber },
      } = this.selectedReservation;
      setAttributeValue(this.order.attributes, "reservationId", _id);
      setAttributeValue(this.order.attributes, "tableNumber", tableNumber);
      await this._prepareOrderCreationData();
      this.display.dialog.reservation.isVisible = false;
      this.display.dialog.orderConfirm.isVisible = true;
      this.display.isLoading = false;
    },
    async onClickOrderConfirm() {
      this.display.isLoading = true;
      try {
        if (
          isDineIn(this.order.mode) &&
          !this.order.isDineInAsap &&
          !this.order.isEventOrder
        ) {
          const selectedTimeMoment = moment(
            this.expectFulfillSelect.TIME_SLOT.dineInSlotMap[
              this.order.expectFulfill.time
            ].value
          );
          const expectFulfillBeforeTime = moment(this.order.expectFulfill.date)
            .hours(selectedTimeMoment.hour())
            .minutes(selectedTimeMoment.minute())
            .seconds(selectedTimeMoment.second())
            .milliseconds(selectedTimeMoment.millisecond())
            .toDate();
          this.order.expectFulfillBeforeTime = expectFulfillBeforeTime;
        }
        const savedOrder = await this._createOrder();
        if (isDineIn(this.order.mode) && !this.order.isDineInAsap) {
          if (this.reservations.length) {
            log.info("Existing reservation, link order to it");
            await linkOrder(this.reservations[0]._id, savedOrder._id);
          } else {
            const reservation = {
              customer: this.order.customer,
              attributes: {
                numberOfCustomers:
                  this.newReservation.attributes.numberOfCustomers,
                orderId: savedOrder._id,
              },
              reservedTime: this.order.expectFulfillBeforeTime,
            };
            await createReservation(reservation);
            log.info("Reservation created for dine-in order");
          }
        }
        await this.$store.dispatch("shoppingCart/createShoppingCart");
        this.$router.push("payment");
      } catch (err) {
        log.error("Order creation error", err);
        const { status, data } = err.response || {};
        let message = "Failed to create an order.";
        if (status === 412) {
          message =
            (data && data.message) ||
            "Some items are already sold out, please head to shopping cart page to remove them.";
        }
        this.$message({
          message,
          type: "error",
        });
      } finally {
        this.display.isLoading = false;
      }
    },

    onClickBack() {
      if (!this.display.isFromPaymentResult) {
        return this.$router.go(-1);
      }
      this.$router.push("/");
    },

    async _createOrder() {
      setAttributeValue(
        this.order.attributes,
        "isPickupAsap",
        this.order.mode === "PICK_UP" && this.order.isPickupAsap
      );
      setAttributeValue(
        this.order.attributes,
        "isDineInAsap",
        this.order.mode === "DINE_IN" && this.order.isDineInAsap
      );
      if (!isDelivery(this.order.mode)) {
        this.order.shippingAddress = null;
      }
      this.order.billingAddress = JSON.parse(
        JSON.stringify(this.order.shippingAddress)
      );
      log.info("Order information", this.order);
      const savedOrder = await create(this.order);
      log.info("Order created", savedOrder._id);
      await this.$store.dispatch("order/refreshLatestOrderId", savedOrder._id);
      return savedOrder;
    },
    async _loadOrder() {
      log.info("Order detail", this.$route.params.id);
      const order = await getById(this.$route.params.id);
      const orderMode = order.attributes.find(
        (attr) => attr.key === "mode"
      ).value;
      order.isPickupAsap = getAttributeValue(order.attributes, "isPickupAsap");
      order.isDineInAsap = getAttributeValue(order.attributes, "isDineInAsap");
      order.mode = orderMode;
      order.modeDisplay = ORDER_MODE.find((m) => m.value === orderMode).display;
      const orderPaymentMethod = order.attributes.find(
        (attr) => attr.key === "paymentMethod"
      ).value;
      order.paymentMethod = orderPaymentMethod;
      order.paymentMethodDisplay = ORDER_PAYMENT_METHOD.find(
        (m) => m.value === orderPaymentMethod
      ).display;
      order.lineItems.forEach(decorateShoppingCartItem);
      if (order.expectFulfillBeforeTime) {
        const dtUpper = moment(order.expectFulfillBeforeTime);
        const dtLower = moment(order.expectFulfillBeforeTime).subtract(
          order.mode === "PICK_UP" ? 15 : 30,
          "minute"
        );
        order.expectFulfill = {
          date: dtUpper.format("D MMM YYYY"),
          time: `${dtLower.format("HH:mma")}-${dtUpper.format("HH:mma")}`,
        };
      } else {
        order.expectFulfill = {
          date: "",
          time: "",
        };
      }
      if (!isNil(order.deliveryAmount)) {
        order.deliveryAmountDisplay = order.deliveryAmount.toFixed(2);
      }
      this.order = order;
      this.display.title = "Order Details";
      this.display.isDetail = true;
      this.subtotal = calculateSubtotal(this.order.lineItems);
      this.amount = this.order.amount.toFixed(2);
      this.paymentSurcharge = this.order.paymentSurcharge;
      this.paymentSurchargeDisplay =
        this.order.paymentSurcharge && this.order.paymentSurcharge.toFixed(2);
      this.order.tableNumber = getAttributeValue(
        this.order.attributes,
        "tableNumber"
      );
      this.order.queueNumber = getAttributeValue(
        this.order.attributes,
        "queueNumber"
      );
    },
    async _loadOrderCoupons() {
      if (isNil(this.order.couponIds) || isEmpty(this.order.couponIds)) {
        return;
      }
      this.order.coupons = (await searchCoupons(this.order.couponIds)).map(
        (coupon) =>
          decorateCoupon(
            coupon,
            this._calculateDiscountableSubtotal(this.order.lineItems, coupon)
          )
      );
    },
    async _loadOrderCreation() {
      if (!this.shoppingCart.shoppingCartId) {
        log.warn("No shopping cart ID, go home");
        return this.$router.replace("/");
      }
      log.info(`Shopping cart ID ${this.shoppingCart.shoppingCartId}`);
      await this.$store.dispatch("shoppingCart/loadShoppingCart");
      if (this.shoppingCart.shoppingCart.lineItems.length <= 0) {
        log.warn("Shopping cart is empty, go home");
        return this.$router.replace("/");
      }
      this.shoppingCart.shoppingCart.lineItems.forEach(
        decorateShoppingCartItem
      );
      this.subtotal = calculateSubtotal(
        this.shoppingCart.shoppingCart.lineItems
      );
      this.order.shoppingCartId = this.shoppingCart.shoppingCartId;

      this.order.isEventOrder = hasEventProduct(
        this.shoppingCart.shoppingCart.lineItems
      );
      this.order.isDineInAsap = !this.order.isEventOrder;
      if (this.token) {
        this.order.customer = await getMe();
      } else if (localStorage.getItem("kingdynasty_ec_options_queue_id")) {
        const queue = await getQueueById(
          localStorage.getItem("kingdynasty_ec_options_queue_id")
        );
        this.order.customer = {
          firstName: queue.firstName || queue.name,
          surname: queue.surname,
          phone: queue.phone,
        };
      }
    },
    async _loadReservation() {
      const reservationId = localStorage.getItem(CONFIG_KEYS.reservationId);
      log.info("Reservation exists, getting reservation", reservationId);
      const reservation = await getReservationById(reservationId);
      const { customer, attributes, reservedTime } = reservation;
      const reservedTimeMoment = moment(reservedTime);

      if (reservedTimeMoment.isBefore(moment())) {
        log.info("Reservation expired, not to prefill");
        return;
      }

      this.reservations.push(reservation);

      this.order.isDineInAsap = false;
      const { firstName, surname, phone } = customer;
      const { numberOfCustomers } = attributes;
      if (isEmpty(this.order.customer)) {
        this.order.customer = {
          firstName,
          surname,
          phone,
        };
      }
      this.order.numberOfCustomers = numberOfCustomers;
      const reservedTimeTime = reservedTimeMoment.format("HH:mma");
      this.order.expectFulfill = {
        date: reservedTimeMoment.format("ddd D MMM YYYY"),
        time: reservedTimeMoment.format("HH:mma"),
      };
      this.expectFulfillSelect.TIME_SLOT.dineInSlotMap = {
        [reservedTimeTime]: {
          display: reservedTimeTime,
          value: reservedTimeMoment.toDate(),
        },
      };
    },
    async _loadAvailableCoupons() {
      let availableCoupons = [];
      if (this.token) {
        availableCoupons = await getMine(true);
      }
      const availablePublicCoupons = await getPublic(true);
      this.availableCoupons = availableCoupons
        .concat(availablePublicCoupons)
        .filter(this._isCouponAvailable)
        .map((coupon) => {
          decorateCoupon(
            coupon,
            this._calculateDiscountableSubtotal(
              this.shoppingCart.shoppingCart.lineItems,
              coupon
            )
          );
          return coupon;
        });
    },
    _isCouponAvailable(coupon) {
      return (
        coupon.status === "VALID" &&
        (isNotNilOrEmpty(coupon.limitation.limitedOrderModes)
          ? coupon.limitation.limitedOrderModes.includes(this.order.mode)
          : true)
      );
    },
    _calculateAmounts() {
      const subtotal = calculateSubtotal(
        this.shoppingCart.shoppingCart.lineItems
      );
      this.subtotal = subtotal;
      const discountAmount = this._calculateDiscountAmount(this.appliedCoupon);
      this.paymentSurcharge =
        this.order.paymentMethod.value === "CREDIT_CARD"
          ? calculateSurcharge(
              subtotal,
              discountAmount,
              this.order.deliveryAmount,
              0.015
            )
          : 0;
      this.paymentSurchargeDisplay = this.paymentSurcharge.toFixed(2);
      this.amount = calculateTotalAmount(
        subtotal,
        this.paymentSurcharge,
        discountAmount,
        this.order.deliveryAmount
      ).toFixed(2);
    },
    _calculateDiscountAmount(coupon) {
      if (!isNotNilOrEmpty(coupon)) {
        return 0;
      }
      const discountableSubtotal = this._calculateDiscountableSubtotal(
        this.shoppingCart.shoppingCart.lineItems,
        coupon
      );
      return this._calculateCouponDiscountAmount(discountableSubtotal, coupon);
    },
    _calculateDiscountableSubtotal(lineItems, coupon) {
      const discountableItems = lineItems.filter((item) =>
        this._isLineItemDiscountable(item, coupon)
      );
      return calculateSubtotal(discountableItems);
    },
    _isLineItemDiscountable(lineItem, coupon) {
      const { limitedProductTypes } = coupon.limitation;
      if (!isNotNilOrEmpty(limitedProductTypes)) {
        return true;
      }
      return limitedProductTypes.includes(lineItem.productType);
    },
    _calculateCouponDiscountAmount(total, coupon) {
      const { discountType, amount, percentage } = coupon.discount;
      if (discountType === "PERCENT") {
        return Number(((total * percentage) / 100).toFixed(2));
      }
      return amount;
    },
    _initNumberOfCustomerSelections(reservationRules) {
      let min = 1;
      let max = 30;
      if (reservationRules) {
        min = reservationRules.numberOfCustomers.min;
        max = reservationRules.numberOfCustomers.max;
      }
      this.newReservation.numberOfCustomersSelection =
        getNumberOfCustomerSelections(min, max);
      if (
        this.newReservation.attributes.numberOfCustomers < min ||
        this.newReservation.attributes.numberOfCustomers > max
      ) {
        this.newReservation.attributes.numberOfCustomers = null;
      }
    },
  },
};
</script>
<style scoped>
.order-container {
  margin-bottom: 2rem;
}

.line-item {
  padding: 0.3rem;
}

.line-item-header {
  border-bottom: 2px solid #221714;
}

.order .field {
  padding-left: 0.6rem;
  padding-right: 0.6rem;
  margin-bottom: 1.2rem;
}

.order .field >>> input,
.order .field >>> textarea {
  border-radius: 0 !important;
  border: 1px solid #c5b899;
}

.order .field >>> .el-radio-group span {
  border-radius: 0 !important;
  border-color: #c5b899;
}

.order .field >>> .el-radio-group .is-active span {
  background-color: #c5b899;
  box-shadow: none;
  color: #221714;
}

.order .field.is-error >>> input {
  border-color: #aa2024;
}

.order .field >>> .el-form-item__error {
  color: #aa2024;
}

.order__mode >>> .el-form-item__error {
  text-align: center;
  position: relative;
}

.buttons {
  margin-top: 1rem;
}

.order__status {
  padding: 0 0.4rem;
}

.field >>> .el-date-editor {
  width: 100%;
}

.line-item__name p {
  margin: 0;
}

.line-item__set-select,
.queue__section {
  margin-top: 0.5rem;
}

.line-item__status {
  display: inline-block;
  padding: 0.1rem 0.2rem;
  border: 1px solid;
  font-size: 0.7rem;
}

.order-radio-block {
  display: block;
  width: 100%;
  text-align: left;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.order-radio-block >>> .el-radio__input {
  left: 0;
  position: absolute;
}

.order-radio-block >>> .el-radio__label {
  padding-left: 4rem;
}

.order-radio-block >>> .is-checked .el-radio__inner {
  background: #c5b899 !important;
}

.order-radio-block.is-checked >>> .el-radio__label {
  font-weight: 900;
  color: #c5b899 !important;
}

.order >>> .el-checkbox__inner {
  border-color: #c5b899;
}

.order >>> .is-checked .el-checkbox__inner {
  background-color: #c5b899;
}

.order >>> .is-checked .el-checkbox__label {
  color: #c5b899;
}

.place-order-btn.is-disabled {
  opacity: 0.5;
}

.info-not-meet-minimum-amount {
  font-size: 0.8rem;
}

.select {
  width: 100%;
}
</style>
